import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../context/app";
import { AppointmentEvent } from "../../../apis/appointmentApi";
import { useGoogleCalendarQuery } from "../../../apis/googleSyncApi.hooks";
import { GoogleCalendarEvent } from "../../../apis/googleSyncApi";
import { CalendarItemSessionTypes } from "../../Calendar/Calendar.utils";
import { useListCalendarItemsQuery } from "../../Calendar/CalendarItemsList/CalendarItemsList-generated.hooks";
import { zonedTimeToUtc } from "date-fns-tz";
import {
  appointmentDateTime,
  calendarItemDateTime,
  getDateRangeForQueries,
  getTimeValueForSort,
  googleCalendarDateTime,
} from "./dailyScheduleUtils";
import { useBatchAppointmentScheduleQuery } from "../../Appointments/AppointmentsStart/AppointmentsStart.hooks";
import { endOfDay, startOfDay } from "date-fns";

type CalendarGQLItems = {
  __typename?: "Class";
  uuid: string;
  title: string;
  startDatetime: string;
  endDatetime: string;
  attendanceCap: number;
  attendanceCount: number | null;
  mainCoach: {
    __typename?: "Profile";
    firstName: string | null;
    lastName: string | null;
  } | null;
  calendarItemType: {
    __typename?: "CalendarItemType";
    name: string | null;
  } | null;
};

export type ScheduleItem = {
  id: string;
  startTime: Date;
  endTime: Date;
  formattedStartTime?: string;
  formattedEndTime?: string;
  title: string;
  link?: string;
  attendanceCount?: number;
} & (
  | { type: "appointment"; data: AppointmentEvent }
  | { type: "googleCalendarEvent"; data: GoogleCalendarEvent }
  | { type: "calendarItem"; data: CalendarGQLItems }
);

export type DailySchedule = {
  dailySchedule: ScheduleItem[];
  isDailyScheduleLoading: boolean;
  isDailyScheduleError: boolean;
  userDailySchedule: ScheduleItem[];
  userDailyScheduleLoading: boolean;
  isGoogleCalendarError: boolean;
};

export const useDailySchedule = (): DailySchedule => {
  const { user, client } = useContext(AppContext);
  const clientTimezone = client?.timezone;
  const { iso, dateOnly } = getDateRangeForQueries(clientTimezone);
  const [isDailyScheduleError, setIsDailyScheduleError] = useState(false);

  const { data: appointmentSchedule, isLoading: appointmentScheduleLoading } =
    useBatchAppointmentScheduleQuery(
      {
        from: zonedTimeToUtc(startOfDay(new Date()), "UTC").toISOString(),
        to: zonedTimeToUtc(endOfDay(new Date()), "UTC").toISOString(),
      },
      {
        onError(error) {
          console.error("Error fetching appointments", error);
          setIsDailyScheduleError(true);
        },
      },
    );

  const {
    data: events,
    isLoading: eventsLoading,
    isError: googleCalendarError,
  } = useGoogleCalendarQuery(client.uuid, iso.start, iso.end);

  const {
    data: calendarGQLItems,
    loading: calendarGQLItemsLoading,
    error: calendarGQLItemsError,
  } = useListCalendarItemsQuery({
    variables: {
      listCalendarItemsInput: {
        clientUuid: client.uuid,
        calendarSessionTypeId: {
          value: CalendarItemSessionTypes.All,
          operator: "EQ",
        },
        calendarItemTypeUuids: undefined,
        coachUuids: undefined,
        // filters
        startDate: dateOnly,
        endDate: dateOnly,
        locationUuid: undefined,
        // pagination
        offset: 0,
        limit: 100,
        page: undefined,
        // sorting
        sortBy: {
          field: "startDatetime",
          type: "ASC",
        },
        isPublicOnly: false,
      },
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (calendarGQLItemsError) {
      setIsDailyScheduleError(true);
    }
  }, [calendarGQLItemsError]);

  const mapAppointmentsToScheduleItems = (
    appointments: AppointmentEvent[],
  ): ScheduleItem[] => {
    // Deduplicate appointments by UUID as the API returns duplicate appointments sometimes for the same class
    const uniqueAppointments = new Map(
      appointments.map((appointment) => [appointment.uuid, appointment]),
    );

    return Array.from(uniqueAppointments.values()).map((appointment) => {
      return {
        id: appointment.uuid,
        startTime: appointmentDateTime.toClientDate(
          appointment.start,
          clientTimezone,
        ),
        endTime: appointmentDateTime.toClientDate(
          appointment.end,
          clientTimezone,
        ),
        formattedStartTime: appointmentDateTime.toClientTimeString(
          appointment.start,
          clientTimezone,
        ),
        formattedEndTime: appointmentDateTime.toClientTimeString(
          appointment.end,
          clientTimezone,
        ),
        title: appointment.appointmentType?.name || "Appointment",
        type: "appointment" as const,
        data: appointment,
        link: "/appointments",
      };
    });
  };

  const mapGoogleCalendarEventsToScheduleItems = (
    events: GoogleCalendarEvent[],
  ): ScheduleItem[] => {
    const today = new Date();
    return events
      .filter((event) => {
        const eventDate = googleCalendarDateTime.toClientDate(
          event.start.dateTime,
          clientTimezone,
        );
        const todaysDate = googleCalendarDateTime.toClientDate(
          today.toISOString(),
          clientTimezone,
        );
        return (
          eventDate.getFullYear() === todaysDate.getFullYear() &&
          eventDate.getMonth() === todaysDate.getMonth() &&
          eventDate.getDate() === todaysDate.getDate()
        );
      })
      .map((event) => ({
        id: event.id,
        startTime: googleCalendarDateTime.toClientDate(
          event.start.dateTime,
          clientTimezone,
        ),
        endTime: googleCalendarDateTime.toClientDate(
          event.end.dateTime,
          clientTimezone,
        ),
        formattedStartTime: googleCalendarDateTime.toClientTimeString(
          event.start.dateTime,
          clientTimezone,
        ),
        formattedEndTime: googleCalendarDateTime.toClientTimeString(
          event.end.dateTime,
          clientTimezone,
        ),
        title: event.name,
        type: "googleCalendarEvent" as const,
        data: event,
      }));
  };

  // Always display CalendarItem times in UTC
  //   (see https://www.notion.so/pushpress/DateTimes-and-Timezones-in-the-Graph-297222589b324242ab59564d635790aa?pvs=4)
  const mapCalendarGQLItemsToScheduleItems = (
    items: CalendarGQLItems[],
  ): ScheduleItem[] => {
    return items.map((item) => ({
      id: item.uuid,
      startTime: calendarItemDateTime.toClientDate(
        item.startDatetime,
        clientTimezone,
      ),
      endTime: calendarItemDateTime.toClientDate(
        item.endDatetime,
        clientTimezone,
      ),
      formattedStartTime: calendarItemDateTime.toClientTimeString(
        item.startDatetime,
        clientTimezone,
      ),
      formattedEndTime: calendarItemDateTime.toClientTimeString(
        item.endDatetime,
        clientTimezone,
      ),
      title: item.title,
      type: "calendarItem" as const,
      attendanceCount: item.attendanceCount,
      data: item,
      link: `/schedule/${item.uuid}`,
    }));
  };

  const dailySchedule: ScheduleItem[] = [
    ...mapAppointmentsToScheduleItems(appointmentSchedule || []),
    ...mapCalendarGQLItemsToScheduleItems(
      calendarGQLItems?.listCalendarItems.items || [],
    ),
  ].sort((a, b) => {
    const aTime = a.startTime.getTime();
    const bTime = b.startTime.getTime();
    return aTime - bTime;
  });

  const isDailyScheduleLoading =
    appointmentScheduleLoading || calendarGQLItemsLoading;

  // TODO: need to handle coaches and assistants for calendar items - currently not getting userUuid for calendar items
  // from gql request.
  const userDailySchedule = [
    ...dailySchedule.filter((item) => {
      if (item.type === "appointment") {
        return item.data.staff.uuid === user.uuid;
      }
      return false;
    }),
    ...mapGoogleCalendarEventsToScheduleItems(events || []),
  ].sort((a, b) => {
    const aTime = getTimeValueForSort(
      a.startTime,
      clientTimezone,
      a.type === "calendarItem",
    );
    const bTime = getTimeValueForSort(
      b.startTime,
      clientTimezone,
      b.type === "calendarItem",
    );
    return aTime - bTime;
  });

  return {
    dailySchedule,
    isDailyScheduleLoading,
    userDailySchedule,
    userDailyScheduleLoading: eventsLoading,
    isDailyScheduleError,
    isGoogleCalendarError: googleCalendarError,
  };
};
